import { useState } from "react";
import "./login.scss";
import toast from "react-hot-toast";
import api from "../../../services/api";

const FieldError = ({ msg }: { msg?: string }) => {
    if (!msg) return null;
    return (
        <div style={{ color: '#E11D48', fontSize: '0.85rem', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
            {msg}
        </div>
    );
};

const Signup = ({ onClose, onNavigateToLogin }: { onClose?: () => void, onNavigateToLogin?: () => void }) => {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [fullName, setFullName] = useState("");
    const [email, setEmail] = useState("");
    const [mobile, setMobile] = useState("");
    const [pan, setPan] = useState("");
    const [dob, setDob] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    
    const [fieldErrors, setFieldErrors] = useState<any>({});

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFieldErrors({});

        let errors: any = {};

        // Validations
        if (fullName.trim().length < 3) {
            errors.fullName = "Full Name must be at least 3 characters.";
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            errors.email = "Please enter a valid email address.";
        }

        const mobileRegex = /^[6-9]\d{9}$/;
        if (!mobileRegex.test(mobile)) {
            errors.mobile = "Please enter a valid 10-digit Indian mobile number.";
        }

        if (pan && pan.trim().length > 0) {
            const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
            if (!panRegex.test(pan.toUpperCase())) {
                errors.pan = "Please enter a valid PAN number format (e.g. ABCDE1234F).";
            }
        }

        if (password.length < 6) {
            errors.password = "Password must be at least 6 characters long.";
        }

        if (password !== confirmPassword) {
            errors.confirmPassword = "Confirm Password does not match.";
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            if(errors.confirmPassword) {
                toast.error("Confirm Password does not match.");
            } else {
                toast.error("Please fill in all required fields.");
            }
            return;
        }

        try {
            const { response, data } = await api.post("/auth/signup", { 
                fullName, email, mobileNumber: mobile, panDetails: pan, dob, password 
            });

            if (response.ok) {
                toast.success(data.message || "Signup successful!");
                if (onNavigateToLogin) {
                    setTimeout(() => onNavigateToLogin(), 2000);
                }
            } else {
                if (data.message && data.message.toLowerCase().includes("email")) {
                    setFieldErrors({email: "This email is already exists"});
                    toast.error("This email already exists. Please use a different email.");
                } else if (data.message && data.message.toLowerCase().includes("mobile")) {
                    setFieldErrors({mobile: "This phone number is already exists"});
                    toast.error("This phone number already exists.");
                } else {
                    toast.error(data.message || "Signup failed");
                }
            }
        } catch (err) {
            toast.error("Error connecting to server.");
        }
    };

    return (
        <div className="login-modal-backdrop" onClick={onClose}>
            <div className="login-popup-card signup-card" onClick={e => e.stopPropagation()}>
                <button className="close-button" onClick={onClose}>
                    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                    </svg>
                </button>
                <div className="popup-header">
                    <h3 className="text-center">Sign Up</h3>
                </div>
                <div className="login-form-section">

                    <form onSubmit={handleSubmit} className="signup-form">
                        <div className="form-group mb-2 mt-2">
                            <label htmlFor="fullname"><span className="star">* </span>Full Name</label>
                            <input 
                                type="text" 
                                id="fullname" 
                                placeholder="Enter a Full Name" 
                                value={fullName} 
                                onChange={e => {setFullName(e.target.value); setFieldErrors({...fieldErrors, fullName: ""})}}
                                style={{ borderColor: fieldErrors.fullName ? '#E11D48' : undefined }}
                                required 
                            />
                            <FieldError msg={fieldErrors.fullName} />
                        </div>

                        <div className="form-group mb-2">
                            <label htmlFor="email"><span className="star">* </span>Email</label>
                            <input 
                                type="email" 
                                id="email" 
                                placeholder="Enter a Email" 
                                value={email} 
                                onChange={e => {setEmail(e.target.value); setFieldErrors({...fieldErrors, email: ""})}}
                                style={{ borderColor: fieldErrors.email ? '#E11D48' : undefined }}
                                required 
                            />
                            <FieldError msg={fieldErrors.email} />
                        </div>

                        <div className="form-group mb-2">
                            <label htmlFor="mobile"><span className="star">* </span>Mobile Number</label>
                            <input 
                                type="tel" 
                                id="mobile" 
                                placeholder="+91" 
                                value={mobile} 
                                onChange={e => {setMobile(e.target.value); setFieldErrors({...fieldErrors, mobile: ""})}}
                                style={{ borderColor: fieldErrors.mobile ? '#E11D48' : undefined }}
                                required 
                            />
                            <FieldError msg={fieldErrors.mobile} />
                        </div>

                        <div className="form-group mb-2">
                            <label htmlFor="pan" style={{ marginBottom: "4px" }}>Pan Details</label>
                            <input 
                                type="text" 
                                id="pan" 
                                placeholder="Enter 10 digit PAN number" 
                                value={pan} 
                                onChange={e => {setPan(e.target.value); setFieldErrors({...fieldErrors, pan: ""})}} 
                                style={{ borderColor: fieldErrors.pan ? '#E11D48' : undefined }}
                            />
                            <FieldError msg={fieldErrors.pan} />
                            {!fieldErrors.pan && <small className="field-hint">(for 80G tax benefits)</small>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="dob">Date Of Birth</label>
                            <div className="input-with-icon">
                                <input type="text" id="dob" placeholder="DD/MM/YYYY" value={dob} onChange={e => setDob(e.target.value)} />
                                <svg className="field-icon" viewBox="0 0 24 24" fill="none" stroke="#263238" strokeWidth="2">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                    <line x1="16" y1="2" x2="16" y2="6"></line>
                                    <line x1="8" y1="2" x2="8" y2="6"></line>
                                    <line x1="3" y1="10" x2="21" y2="10"></line>
                                </svg>
                            </div>
                        </div>

                        <div className="form-group mb-2">
                            <label htmlFor="password"><span className="star">* </span>Password</label>
                            <div className="input-with-icon">
                                <input 
                                    type={showPassword ? "text" : "password"} 
                                    id="password" 
                                    placeholder="Enter a Password" 
                                    value={password} 
                                    onChange={e => {setPassword(e.target.value); setFieldErrors({...fieldErrors, password: ""})}}
                                    style={{ borderColor: fieldErrors.password ? '#E11D48' : undefined }}
                                    required 
                                />
                                <svg className="field-icon eye-icon" onClick={() => setShowPassword(!showPassword)} viewBox="0 0 24 24" fill="none" stroke="#263238" strokeWidth="2" style={{cursor: "pointer"}}>
                                    {showPassword ? (
                                        <>
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                            <circle cx="12" cy="12" r="3"></circle>
                                        </>
                                    ) : (
                                        <>
                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                            <line x1="1" y1="1" x2="23" y2="23"></line>
                                        </>
                                    )}
                                </svg>
                            </div>
                            <FieldError msg={fieldErrors.password} />
                        </div>

                        <div className="form-group mb-4">
                            <label htmlFor="confirmPassword"><span className="star">* </span>Confirm Password</label>
                            <div className="input-with-icon">
                                <input 
                                    type={showConfirmPassword ? "text" : "password"} 
                                    id="confirmPassword" 
                                    placeholder="Enter a Confirm Password" 
                                    value={confirmPassword} 
                                    onChange={e => {setConfirmPassword(e.target.value); setFieldErrors({...fieldErrors, confirmPassword: ""})}}
                                    style={{ borderColor: fieldErrors.confirmPassword ? '#E11D48' : undefined }}
                                    required 
                                />
                                <svg className="field-icon eye-icon" onClick={() => setShowConfirmPassword(!showConfirmPassword)} viewBox="0 0 24 24" fill="none" stroke="#263238" strokeWidth="2" style={{cursor: "pointer"}}>
                                    {showConfirmPassword ? (
                                        <>
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                            <circle cx="12" cy="12" r="3"></circle>
                                        </>
                                    ) : (
                                        <>
                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                            <line x1="1" y1="1" x2="23" y2="23"></line>
                                        </>
                                    )}
                                </svg>
                            </div>
                            <FieldError msg={fieldErrors.confirmPassword} />
                        </div>

                        <button type="submit" className="login-submit-btn signup-btn mt-4">Sign Up</button>
                    </form>

                    <div className="signup-link">
                        Already have an account? <a href="#login" onClick={(e) => { e.preventDefault(); if (onNavigateToLogin) onNavigateToLogin(); }}>Login</a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Signup;